import Checkbox from "@/Components/Checkbox";
import InputError from "@/Components/InputError";
import InputLabel from "@/Components/InputLabel";
import PrimaryButton from "@/Components/PrimaryButton";
import SecondaryButton from "@/Components/SecondaryButton";
import TextInput from "@/Components/TextInput";
import GuestLayout from "@/Layouts/GuestLayout";
import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { FormEventHandler, useEffect, useState } from "react";
import PasswordInput from "@/Components/Inputs/PasswordInput";

import backgroundImage from "../../../../public/login-background.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClinicMedical,
    faPercentage,
    faUser,
    faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmOrChangeEmailModal from "@/Components/Modals/ConfirmOrChangeEmailModal";
import TertiaryButton from "@/Components/TertiaryButton";

export default function Login({
    status,
    canResetPassword,
    selectedPortal,
    foundEmail,
    adminEmailRequired,
}: {
    status?: string;
    canResetPassword: boolean;
    selectedPortal:
        | "patient"
        | "provider"
        | "admin"
        | "rxdc"
        | "passport"
        | string;
    foundEmail: string | null;
    adminEmailRequired: boolean | null;
}) {
    const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
    const { flash, features } = usePage().props;

    const { data, setData, post, processing, errors, reset } = useForm({
        email: "",
        password: "",
        remember: false,
        portal: selectedPortal || "patient",
    });
    useEffect(() => {
        if (
            data.portal == "admin" &&
            adminEmailRequired != undefined &&
            (flash.message === "Please provide an email address" ||
                flash.message ===
                    "We found the following email attached to this account")
        ) {
            setData(
                "email",
                !data.email.includes("@") && foundEmail?.includes("@")
                    ? foundEmail
                    : !data.email.includes("@")
                      ? ""
                      : data.email,
            );
            setShowEmailModal(true);
        }
    }, [flash, foundEmail, adminEmailRequired]);

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        post(route("login"), {
            preserveState: true,
        });
    };

    return (
        <GuestLayout backgroundImagePath={backgroundImage}>
            <Head title="Log in" />

            {status && (
                <div className="mb-4 font-medium text-green-600 text-center">
                    {status}
                </div>
            )}

            <form onSubmit={submit} className="">
                <div className="mb-4">
                    <h1 className="text-2xl text-bold text-center">
                        Sav-Rx Passport
                    </h1>
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="portal-select" value="I am a..." />
                    <div
                        id="portal-select"
                        className="md:flex-row flex flex-col gap-2"
                    >
                        {features?.login?.patient.enabled && (
                            <PrimaryButton
                                data-dusk="i-am-a-patient-button"
                                className={`w-full `}
                                success={data.portal === "patient"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setData("portal", "patient");
                                }}
                            >
                                <FontAwesomeIcon icon={faUser} size={"2x"} />
                                Patient
                            </PrimaryButton>
                        )}

                        {features?.login.admin.enabled && (
                            <PrimaryButton
                                data-dusk="i-am-a-admin-button"
                                className={`w-full`}
                                success={data.portal === "admin"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setData("portal", "admin");
                                }}
                            >
                                <FontAwesomeIcon icon={faUserTie} size={"2x"} />
                                Plan Administrator
                            </PrimaryButton>
                        )}

                        {features?.login.provider.enabled && (
                            <PrimaryButton
                                data-dusk="i-am-a-provider-button"
                                className={`w-full`}
                                success={data.portal === "provider"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setData("portal", "provider");
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faClinicMedical}
                                    size={"2x"}
                                />
                                Pharmacy
                            </PrimaryButton>
                        )}

                        {features?.login.rxdc.enabled && (
                            <PrimaryButton
                                data-dusk="i-am-a-rxdc-button"
                                className={`w-full`}
                                success={data.portal === "rxdc"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setData("portal", "rxdc");
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faPercentage}
                                    size={"2x"}
                                />
                                RxDC Administrator
                            </PrimaryButton>
                        )}
                    </div>
                    <InputError message={errors.portal} className="mt-2" />
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="email" value="Email" />
                    <TextInput
                        data-dusk="email-input"
                        id="email"
                        name="email"
                        className="mt-1 block w-full"
                        autoComplete="off"
                        isFocused={true}
                        onChange={(e) => setData("email", e.target.value)}
                    />

                    <InputError message={errors.email} className="mt-2" />
                </div>

                <div className="mt-4">
                    <InputLabel htmlFor="password" value="Password" />

                    <div className="flex">
                        <PasswordInput
                            password={data.password}
                            onChange={(e) =>
                                setData("password", e.target.value)
                            }
                        />
                    </div>
                    <InputError message={errors.password} className="mt-2" />
                </div>

                <div className="mt-4 flex items-center justify-between">
                    <label className="flex items-center">
                        <Checkbox
                            name="remember"
                            checked={data.remember}
                            onChange={(e) =>
                                setData("remember", e.target.checked)
                            }
                        />
                        <span className="ms-2 text-sm text-gray-600">
                            Stay signed in for 14 days
                        </span>
                    </label>
                    {canResetPassword && (
                        <Link
                            href={route("password.request")}
                            className="rounded-md text-sm text-gray-600 underline hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                            Forgot your password?
                        </Link>
                    )}
                </div>
                <div className="mt-2 text-center">
                    <PrimaryButton
                        data-dusk="login-button"
                        className="w-full justify-center"
                        disabled={processing}
                        type="submit"
                    >
                        Log in
                    </PrimaryButton>
                    {data.portal === "patient" && (
                        <Link href={route("quick-refill")}>
                            <TertiaryButton
                                className="w-full justify-center mt-2 uppercase"
                                data-dusk="mail-order-button"
                            >
                                Mail Order Quick Refill
                            </TertiaryButton>
                        </Link>
                    )}
                    <Link href={route("register")}>
                        <SecondaryButton className="w-full justify-center mt-2 uppercase">
                            Don't Have a Sav-Rx Login? Click Here
                        </SecondaryButton>
                    </Link>
                </div>
                <ConfirmOrChangeEmailModal
                    open={showEmailModal}
                    email={data.email}
                    error={errors?.email}
                    emailRequired={adminEmailRequired}
                    onChange={(email) => {
                        setData("email", email);
                    }}
                />
            </form>
        </GuestLayout>
    );
}
